// ─── Editorial Romance V1 ─────────────────────────────────────
// Romantic editorial wedding invitation. Rose-beige palette,
// Cormorant Garamond headings, cinematic full-screen hero,
// asymmetric gallery, refined venue card.

export const editorialRomanceV1 = {
  templateId: 'editorial-romance-v1',
  name: 'Editorial Romance',
  description: 'Romantic editorial style. Cinematic hero, soft rose-beige tones.',
  category: 'wedding',
  previewColors: ['#F6EFEC', '#C88F8F', '#2A2220'],
  theme: {
    palette: 'rose-beige',
    fontPair: 'cormorant-manrope',
    radius: 'soft',
    shadow: 'soft',
    overlay: 'none',
  },
  settings: {
    musicEnabled: false,
    countdownEnabled: false,
    rsvpEnabled: true,
    showMapLink: true,
  },
  sections: [
    {
      id: 'hero-1',
      type: 'hero',
      variant: 'hero-cinematic',
      visible: true,
      animation: {
        title: 'stagger-text',
        subtitle: 'fade-up',
        image: 'zoom-soft',
      },
      editableRules: {
        fields: ['introLabel', 'title', 'subtitle', 'dateText', 'backgroundImage', 'ctaText'],
        locked: ['layout', 'spacing', 'typographyScale'],
      },
      data: {
        introLabel: 'Editorial Romance',
        title: 'Amelia & Luca',
        subtitle: 'A beautiful celebration of love, light, and the moments that stay with us forever.',
        dateText: '05 June 2026 · Lake Como',
        backgroundImage: 'https://images.unsplash.com/photo-1519741497674-611481863552?q=80&w=1600&auto=format&fit=crop',
        ctaText: 'Discover the day',
      },
    },
    {
      id: 'quote-1',
      type: 'quote',
      variant: 'quote-centered',
      visible: true,
      animation: { section: 'fade-in' },
      editableRules: {
        fields: ['quoteText'],
        locked: ['layout', 'spacing'],
      },
      data: {
        quoteText: 'Every love story is beautiful, but ours is our favorite.',
      },
    },
    {
      id: 'story-1',
      type: 'story',
      variant: 'story-image-left',
      visible: true,
      animation: { text: 'fade-up', image: 'slide-right' },
      editableRules: {
        fields: ['heading', 'title', 'paragraph', 'secondaryParagraph', 'image'],
        locked: ['layout', 'spacing'],
      },
      data: {
        heading: 'Our Story',
        title: 'A chapter written in quiet moments',
        paragraph: 'From our very first conversation, something felt effortless. Over time, laughter became home, and the simplest days became the most meaningful.',
        secondaryParagraph: 'We would be honored to celebrate this next chapter surrounded by the people we love most.',
        image: 'https://images.unsplash.com/photo-1515934751635-c81c6bc9a2d8?q=80&w=1200&auto=format&fit=crop',
      },
    },
    {
      id: 'timeline-1',
      type: 'timeline',
      variant: 'timeline-centered',
      visible: true,
      animation: { items: 'stagger-cards' },
      editableRules: {
        fields: ['items'],
        locked: ['connectorStyle', 'spacing', 'layout'],
      },
      data: {
        items: [
          { time: '16:00', title: 'Ceremony', description: 'An intimate lakeside ceremony as the sun begins to soften.' },
          { time: '18:00', title: 'Dinner', description: 'A long-table dinner with candlelight, conversation, and music.' },
          { time: '21:00', title: 'Dancing', description: 'An evening of celebration under the stars.' },
        ],
      },
    },
    {
      id: 'gallery-1',
      type: 'gallery',
      variant: 'gallery-editorial',
      visible: true,
      animation: { items: 'zoom-soft' },
      editableRules: {
        fields: ['images'],
        locked: ['grid', 'gap', 'imageRatio'],
      },
      data: {
        images: [
          'https://images.unsplash.com/photo-1522673607200-164d1b6ce486?q=80&w=900&auto=format&fit=crop',
          'https://images.unsplash.com/photo-1519225421980-715cb0215aed?q=80&w=900&auto=format&fit=crop',
          'https://images.unsplash.com/photo-1520854221256-17451cc331bf?q=80&w=900&auto=format&fit=crop',
        ],
      },
    },
    {
      id: 'venue-1',
      type: 'venue',
      variant: 'venue-card-refined',
      visible: true,
      animation: { section: 'fade-up' },
      editableRules: {
        fields: ['venueName', 'address', 'mapLink', 'note', 'image'],
        locked: ['layout', 'spacing', 'buttonStyle'],
      },
      data: {
        venueName: 'Villa Aurelia',
        address: 'Lake Como, Italy',
        note: 'A romantic setting overlooking the water, with dinner and celebration to follow.',
        mapLink: 'https://maps.google.com',
        image: 'https://images.unsplash.com/photo-1519167758481-83f550bb49b3?q=80&w=1200&auto=format&fit=crop',
      },
    },
    {
      id: 'rsvp-1',
      type: 'rsvp',
      variant: 'rsvp-soft-card',
      visible: true,
      animation: { section: 'fade-up' },
      editableRules: {
        fields: ['title', 'description', 'submitLabel'],
        locked: ['validationRules', 'inputStyle', 'spacing'],
      },
      data: {
        title: 'RSVP',
        description: 'Please let us know if you\'ll be joining us by 10 May 2026.',
        submitLabel: 'Send RSVP',
      },
    },
    {
      id: 'footer-1',
      type: 'footer',
      variant: 'footer-centered',
      visible: true,
      animation: { section: 'fade-in' },
      editableRules: {
        fields: ['closingMessage', 'names'],
        locked: ['layout', 'spacing'],
      },
      data: {
        closingMessage: 'We cannot wait to share this day with you.',
        names: 'Amelia & Luca',
      },
    },
  ],
};
